import {of as observableOf,  Observable } from 'rxjs';

import { map } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { HttpService } from './http.service';
import {
  ISensorData,
  IUltraSonicData,
  ISensorDataParams,
  IUrineTrendData,
  IActivityData,
  IVisualizedUrineVolume,
  IVisualizedUrineVolumeParams,
  IBladderWaveform,
  IBladderWaveformParams,
  IUrineReaction,
  IUrineReactionParams,
  ISpotCheck,
  ISpotCheckParams,
} from '../_interfaces';

@Injectable()
export class SensorService {
  constructor(private http: HttpService) {}

  fetchSensorData({
    from = null,
    to = null,
    residentId = null,
  }: Partial<ISensorDataParams>): Observable<ISensorData[]> {
    if (!from || !to || !residentId) {
      return observableOf([]);
    }

    const params: { [key: string]: any } = {
      residentId: residentId,
      from: from,
      to: to,
    };
    return this.http.get<ISensorData[]>(
      '/api/v2/pro/traversal/residents/:residentId/sensor-reaction-resamples',
      params
    );
  }

  fetchRawData({
    from = null,
    to = null,
    residentId = null,
  }: Partial<ISensorDataParams>): Observable<IUltraSonicData[]> {
    if (!from || !to || !residentId) {
      return observableOf([]);
    }

    const params: { [key: string]: any } = {
      from: from,
      to: to,
      residentId: residentId,
      limit: 50000,
    };

    return this.http
      .get<IUltraSonicData[]>(
        '/api/v2/pro/traversal/residents/:residentId/ultra-sonic-data',
        params
      )
      .pipe(
        map((res: IUltraSonicData[]) => {
          return res;
        })
      );
  }

  fetchUrineTrendData({
    from = null,
    to = null,
    residentId = null,
    limit = 100000,
  }): Observable<IUrineTrendData[]> {
    if (!from || !to || !residentId) {
      return observableOf([]);
    }

    const params: { [key: string]: any } = {
      from: from,
      to: to,
      residentId: residentId,
      limit: limit,
    };

    return this.http
      .get<any[]>(
        '/api/v2/pro/residents/:residentId/urine-trend-analysis',
        params
      )
      .pipe(
        map((res: IUrineTrendData[]) => {
          return res;
        })
      );
  }

  fetchActivityData({
    from = null,
    to = null,
    residentId = null,
    limit = 100000,
  }): Observable<IActivityData[]> {
    if (!from || !to || !residentId) {
      return observableOf([]);
    }

    const params: { [key: string]: any } = {
      from: from,
      to: to,
      residentId: residentId,
      limit: limit,
    };

    return this.http
      .get<any[]>(
        '/api/v2/pro/residents/:residentId/activity-status-analysis',
        params
      )
      .pipe(
        map((res: IActivityData[]) => {
          return res;
        })
      );
  }

  fetchVisualizedUrineVolumeData({
    from = null,
    to = null,
    residentId = null,
    limit = null,
  }: Partial<IVisualizedUrineVolumeParams>): Observable<IVisualizedUrineVolume[]> {
    if (!from || !to || !residentId) {
      return observableOf([]);
    }

    const params: { [key: string]: any } = {
      residentId: residentId,
      from: from,
      to: to,
      limit: limit,
    };
    return this.http.get<IVisualizedUrineVolume[]>(
      '/api/v2/pro/md/residents/:residentId/visualized-urine-volume',
      params
    );
  }

  fetchBladderWaveformData({
    from = null,
    to = null,
    residentId = null,
    limit = null,
  }: Partial<IBladderWaveformParams>): Observable<IBladderWaveform[]> {
    if (!from || !to || !residentId) {
      return observableOf([]);
    }

    const params: { [key: string]: any } = {
      residentId: residentId,
      from: from,
      to: to,
      limit: limit,
    };
    return this.http.get<IBladderWaveform[]>(
      '/api/v2/pro/md/residents/:residentId/bladder-waveform',
      params
    );
  }
  fetchUrineReactionData({
    from = null,
    to = null,
    residentId = null,
    limit = null,
  }: Partial<IUrineReactionParams>): Observable<IUrineReaction[]> {
    if (!from || !to || !residentId) {
      return observableOf([]);
    }

    const params: { [key: string]: any } = {
      residentId: residentId,
      from: from,
      to: to,
      limit: limit,
    };
    return this.http.get<IUrineReaction[]>(
      '/api/v2/pro/md/residents/:residentId/urine-reaction',
      params
    );
  }

  fetchSpotCheckData(residentId = null, limit = null, sort = null, from = null, to = null): Observable<ISpotCheck[]> {
    if (!residentId) {
      return observableOf([]);
    }

    let params: { [key: string]: any } = {};

    if (residentId) {
      params = { ...params, residentId};
    }
    if (limit) {
      params = { ...params, limit };
    }
    if (sort) {
      params = { ...params, sort };
    }
    if (from && to) {
      params = { ...params, from, to };
    }

    return this.http.get<ISpotCheck[]>(
      '/api/v2/pro/md/residents/:residentId/spot-check',
      params
    );
  }

  fetchSpotCheckUsdData(residentId, timestamp): Observable<ISpotCheck> {
    if (!residentId || !timestamp) {
      return observableOf();
    }

    let params: { [key: string]: any } = {};

    if (timestamp) {
      params = { ...params, timestamp};
    }

    return this.http.get<ISpotCheck>(
      '/api/v2/pro/md/residents/' + residentId + '/spot-check-usd',
      params
    );
  }
}
